/* Changing a form without a release, and without the ERPNext desk.
 *
 * The engine draws every form from metadata. This is the other half of that
 * promise, from the admin's side: add a field here, open the form, it is
 * there. No deployment, no code, no ticket.
 *
 * Written for somebody who runs a business, not somebody who knows ERPNext:
 * "A line of text" rather than `Data`, "What it is called" rather than
 * `label`, and a plain sentence about what happens to the data when a field
 * is taken away — because "delete" reads like the typing goes with it.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useErp } from '@jackdesk/erp-kit'
import type { CustomiseSurface, ErpError } from '@jackdesk/erp-kit'
import { Failed, Loading, useToast } from '@jackdesk/shell'
import index from './doctypes.generated.json'
import { labelFor } from './nav'

export function Customise() {
  const { doctype = '' } = useParams()
  const navigate = useNavigate()
  const dt = decodeURIComponent(doctype)
  return dt ? <CustomiseOne doctype={dt} onOpen={(d) => navigate(`/customise/${encodeURIComponent(d)}`)} />
            : <PickAForm onOpen={(d) => navigate(`/customise/${encodeURIComponent(d)}`)} />
}

function PickAForm({ onOpen }: { onOpen: (doctype: string) => void }) {
  const [text, setText] = useState('')
  const found = useMemo(() => {
    const needle = text.trim().toLowerCase()
    return index
      .filter((d) => !d.admin && !d.g)
      .map((d) => ({ name: d.n, label: labelFor(d.n), module: d.m }))
      .filter((d) => !needle || d.label.toLowerCase().includes(needle)
                             || d.name.toLowerCase().includes(needle))
      .slice(0, 60)
  }, [text])

  return (
    <div className="page" style={{ display: 'grid', gap: 16 }}>
      <div>
        <h1>Change a form</h1>
        <p className="muted small" style={{ marginTop: 4, maxWidth: '62ch' }}>
          Add a field your business needs, rename one that is called the wrong thing,
          or make one compulsory. Changes show up the next time somebody opens the
          form — there is nothing to install and nothing to wait for.
        </p>
      </div>
      <input className="input" style={{ maxWidth: 320 }} value={text}
             placeholder="Which form?" aria-label="Which form"
             onChange={(e) => setText(e.target.value)} />
      <div style={{ display: 'grid', gap: 6, gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))' }}>
        {found.map((d) => (
          <button key={d.name} className="card card-pad" style={{ textAlign: 'left', cursor: 'pointer' }}
                  onClick={() => onOpen(d.name)}>
            <div style={{ fontWeight: 560 }}>{d.label}</div>
            <div className="small muted">{d.module}</div>
          </button>
        ))}
      </div>
    </div>
  )
}

function CustomiseOne({ doctype, onOpen }: { doctype: string; onOpen: (d: string) => void }) {
  const erp = useErp()
  const toast = useToast()
  const navigate = useNavigate()
  const [data, setData] = useState<CustomiseSurface | null>(null)
  const [failed, setFailed] = useState<ErpError | null>(null)
  const [busy, setBusy] = useState(false)

  const load = useCallback(() => {
    setFailed(null)
    erp.customiseSurface(doctype).then(setData).catch((e) => setFailed(e as ErpError))
  }, [erp, doctype])
  useEffect(load, [load])

  // The new field being described.
  const [label, setLabel] = useState('')
  const [fieldtype, setFieldtype] = useState('Data')
  const [options, setOptions] = useState('')
  const [required, setRequired] = useState(false)
  const [inList, setInList] = useState(false)

  if (failed) return <div className="page"><Failed message={failed.message} requestId={failed.requestId} onRetry={load} /></div>
  if (!data) return <div className="page"><Loading rows={6} label={`Reading the ${labelFor(doctype)} form`} /></div>

  const needsOptions = fieldtype === 'Link' || fieldtype === 'Select'

  const add = async () => {
    setBusy(true)
    try {
      await erp.addCustomField(doctype, {
        label, fieldtype, options: options.trim() || undefined,
        required, in_list: inList,
      })
      toast('ok', `“${label}” is on the ${labelFor(doctype)} form now.`)
      setLabel(''); setOptions(''); setRequired(false); setInList(false)
      load()
    } catch (e) { toast('error', (e as ErpError).message) }
    finally { setBusy(false) }
  }

  return (
    <div className="page" style={{ display: 'grid', gap: 16, maxWidth: 900 }}>
      <div className="row-between">
        <div>
          <button className="linkish small" onClick={() => onOpen('')}>← All forms</button>
          <h1 style={{ marginTop: 4 }}>{labelFor(doctype)}</h1>
          <p className="muted small" style={{ marginTop: 4 }}>
            {data.fields.length} fields as it comes, {data.custom_fields.length} you have added.
          </p>
        </div>
        <button className="btn" onClick={() => navigate(`/doc/${encodeURIComponent(doctype)}/new`)}>
          See the form
        </button>
      </div>

      {/* ── add ─────────────────────────────────────────────────────── */}
      <section className="card card-pad stack" style={{ gap: 10 }}>
        <strong className="small">Add a field</strong>
        <div className="row" style={{ gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <label className="field" style={{ minWidth: 200 }}>
            <span style={{ fontSize: 12.5, fontWeight: 550, color: 'var(--ink-2)' }}>
              What is it called?
            </span>
            <input className="input" value={label} placeholder="Purchase order number"
                   onChange={(e) => setLabel(e.target.value)} />
          </label>
          <label className="field" style={{ minWidth: 190 }}>
            <span style={{ fontSize: 12.5, fontWeight: 550, color: 'var(--ink-2)' }}>
              What goes in it?
            </span>
            <select className="select" value={fieldtype}
                    onChange={(e) => setFieldtype(e.target.value)}>
              {data.addable.map((a) => (
                <option key={a.fieldtype} value={a.fieldtype}>{a.label}</option>
              ))}
            </select>
          </label>
          {needsOptions ? (
            <label className="field" style={{ minWidth: 200 }}>
              <span style={{ fontSize: 12.5, fontWeight: 550, color: 'var(--ink-2)' }}>
                {fieldtype === 'Link' ? 'A link to which records?' : 'The choices, one to a line'}
              </span>
              {fieldtype === 'Link'
                ? <input className="input" value={options} placeholder="Customer"
                         onChange={(e) => setOptions(e.target.value)} />
                : <textarea className="input" rows={3} value={options} placeholder={'Red\nAmber\nGreen'}
                            onChange={(e) => setOptions(e.target.value)} />}
            </label>
          ) : null}
        </div>
        <div className="row" style={{ gap: 16, flexWrap: 'wrap' }}>
          <label className="row" style={{ gap: 6 }}>
            <input type="checkbox" checked={required} onChange={(e) => setRequired(e.target.checked)} />
            <span className="small">It has to be filled in</span>
          </label>
          <label className="row" style={{ gap: 6 }}>
            <input type="checkbox" checked={inList} onChange={(e) => setInList(e.target.checked)} />
            <span className="small">Show it as a column in the list</span>
          </label>
          <span className="grow" />
          <button className="btn btn-primary" disabled={busy || !label.trim()} onClick={() => void add()}>
            {busy ? 'Adding…' : 'Add it'}
          </button>
        </div>
        <p className="small muted" style={{ margin: 0 }}>
          It appears the next time somebody opens the form. Nothing is installed.
        </p>
      </section>

      {/* ── what has been added ─────────────────────────────────────── */}
      {data.custom_fields.length ? (
        <section className="card">
          <p className="small muted" style={{ padding: '10px 12px 0', margin: 0 }}>
            Fields you have added. Taking one away hides it and keeps whatever was typed
            into it, so putting it back brings the answers back too.
          </p>
          <table className="tbl">
            <thead><tr><th>Field</th><th>What goes in it</th><th></th><th></th></tr></thead>
            <tbody>
              {data.custom_fields.map((f) => (
                <tr key={f.name} style={{ cursor: 'default' }}>
                  <td>{f.label || f.fieldname}<span className="small muted"> · {f.fieldname}</span></td>
                  <td>{data.addable.find((a) => a.fieldtype === f.fieldtype)?.label ?? f.fieldtype}
                      {f.options ? <span className="small muted"> · {f.options.split('\n')[0]}</span> : null}</td>
                  <td>{f.reqd ? <span className="chip">must be filled in</span> : null}
                      {f.in_list_view ? <span className="chip">a column</span> : null}</td>
                  <td>
                    <button className="btn btn-sm" onClick={async () => {
                      await erp.removeCustomField(doctype, f.name)
                      toast('ok', `“${f.label || f.fieldname}” is off the form.`)
                      load()
                    }}>Take it away</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ) : null}

      {/* ── rename / require an existing one ────────────────────────── */}
      <ChangeAField surface={data} doctype={doctype} onDone={load} />
    </div>
  )
}

function ChangeAField({ surface, doctype, onDone }: {
  surface: CustomiseSurface; doctype: string; onDone: () => void
}) {
  const erp = useErp()
  const toast = useToast()
  const [field, setField] = useState('')
  const [property, setProperty] = useState('label')
  const [value, setValue] = useState('')
  const isYesNo = ['reqd', 'hidden', 'read_only', 'in_list_view', 'in_standard_filter']
    .includes(property)

  return (
    <section className="card card-pad stack" style={{ gap: 10 }}>
      <strong className="small">Change a field that is already there</strong>
      <div className="row" style={{ gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <label className="field" style={{ minWidth: 220 }}>
          <span style={{ fontSize: 12.5, fontWeight: 550, color: 'var(--ink-2)' }}>Which field</span>
          <select className="select" value={field} onChange={(e) => setField(e.target.value)}>
            <option value="">Choose one…</option>
            {surface.fields.map((f) => (
              <option key={f.fieldname} value={f.fieldname}>{f.label || f.fieldname}</option>
            ))}
          </select>
        </label>
        <label className="field" style={{ minWidth: 200 }}>
          <span style={{ fontSize: 12.5, fontWeight: 550, color: 'var(--ink-2)' }}>What about it</span>
          <select className="select" value={property} onChange={(e) => setProperty(e.target.value)}>
            {surface.changeable.map((c) => (
              <option key={c.property} value={c.property}>{c.label}</option>
            ))}
          </select>
        </label>
        <label className="field" style={{ minWidth: 180 }}>
          <span style={{ fontSize: 12.5, fontWeight: 550, color: 'var(--ink-2)' }}>To</span>
          {isYesNo
            ? <select className="select" value={value} onChange={(e) => setValue(e.target.value)}>
                <option value="1">Yes</option><option value="0">No</option>
              </select>
            : <input className="input" value={value} onChange={(e) => setValue(e.target.value)} />}
        </label>
        <button className="btn btn-primary" disabled={!field}
                onClick={async () => {
                  try {
                    await erp.changeField(doctype, { fieldname: field, property, value })
                    toast('ok', 'Changed. Open the form to see it.')
                    setField(''); setValue(''); onDone()
                  } catch (e) { toast('error', (e as ErpError).message) }
                }}>Change it</button>
      </div>

      {surface.changes.length ? (
        <>
          <p className="small muted" style={{ margin: '4px 0 0' }}>
            What you have changed. Undoing one puts the field back exactly as ERPNext ships it.
          </p>
          <table className="tbl">
            <thead><tr><th>Field</th><th>Change</th><th>To</th><th></th></tr></thead>
            <tbody>
              {surface.changes.map((c) => (
                <tr key={c.name} style={{ cursor: 'default' }}>
                  <td>{surface.fields.find((f) => f.fieldname === c.field_name)?.label ?? c.field_name}</td>
                  <td>{surface.changeable.find((x) => x.property === c.property)?.label ?? c.property}</td>
                  <td>{c.value === '1' ? 'Yes' : c.value === '0' ? 'No' : c.value}</td>
                  <td><button className="btn btn-sm" onClick={async () => {
                    await erp.undoChange(doctype, c.name); toast('ok', 'Put back.'); onDone()
                  }}>Undo</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      ) : null}
    </section>
  )
}
